import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { TreePine, Plus, X, Trash2, PenLine, Eye, EyeOff, Users, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SEASON_COLORS = {
  season_1: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20" },
  season_2: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20" },
  season_3: { bg: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/20" }
};

export default function AdminRetreat() {
  const [tab, setTab] = useState("modules");
  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [seasons, setSeasons] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterSeason, setFilterSeason] = useState("");
  const [form, setForm] = useState({ season: "season_1", title: "", description: "", week_number: 1, goals: "", activities: "", milestones: "", content: "", status: "draft" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSeason) params.season = filterSeason;
      const [modRes, statsRes, enrRes] = await Promise.all([
        apiClient.get('/admin/retreat/modules', { params }),
        apiClient.get('/admin/retreat/stats'),
        apiClient.get('/admin/retreat/enrollments')
      ]);
      setModules(modRes.data.modules);
      setSeasons(modRes.data.seasons || {});
      setStats(statsRes.data);
      setEnrollments(enrRes.data.enrollments);
    } catch (err) { console.error('Failed to fetch retreat data:', err); }
    finally { setLoading(false); }
  }, [filterSeason]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ season: "season_1", title: "", description: "", week_number: 1, goals: "", activities: "", milestones: "", content: "", status: "draft" });
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      goals: form.goals.split("\n").filter(Boolean),
      activities: form.activities.split("\n").filter(Boolean),
      milestones: form.milestones.split("\n").filter(Boolean)
    };
    try {
      if (editId) {
        await apiClient.put(`/admin/retreat/modules/${editId}`, payload);
      } else {
        await apiClient.post('/admin/retreat/modules', payload);
      }
      resetForm();
      fetchData();
    } catch (err) { console.error('Failed to save module:', err); }
    finally { setSaving(false); }
  };

  const startEdit = (m) => {
    setForm({
      season: m.season, title: m.title, description: m.description, week_number: m.week_number,
      goals: (m.goals || []).join("\n"), activities: (m.activities || []).join("\n"),
      milestones: (m.milestones || []).join("\n"), content: m.content || "", status: m.status
    });
    setEditId(m.module_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this module?")) return;
    try { await apiClient.delete(`/admin/retreat/modules/${id}`); fetchData(); }
    catch (err) { console.error('Failed to delete module:', err); }
  };

  const toggleStatus = async (m) => {
    try {
      await apiClient.put(`/admin/retreat/modules/${m.module_id}`, { status: m.status === "published" ? "draft" : "published" });
      fetchData();
    } catch (err) { console.error('Failed to toggle module status:', err); }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-retreat">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-1">ME.WELL Mind-SPA Retreat</h1>
            <p className="text-white/40 text-sm">Annual Continuum of Care programming</p>
          </div>
          <Button onClick={() => showForm ? resetForm() : setShowForm(true)} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2" data-testid="new-module-btn">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "Add Module"}
          </Button>
        </div>

        {/* Stats */}
        {!loading && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <p className="text-2xl font-semibold text-white">{stats.total_modules}</p>
              <p className="text-white/40 text-xs">Total Modules</p>
            </div>
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <p className="text-2xl font-semibold text-emerald-400">{stats.published}</p>
              <p className="text-white/40 text-xs">Published</p>
            </div>
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <p className="text-2xl font-semibold text-amber-400">{stats.enrolled_users}</p>
              <p className="text-white/40 text-xs">Enrolled Users</p>
            </div>
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <p className="text-2xl font-semibold text-white">{Object.keys(stats.by_season || {}).length}/3</p>
              <p className="text-white/40 text-xs">Seasons with Content</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("modules")} className={`px-4 py-2 rounded-xl text-sm transition-all ${tab === "modules" ? "bg-amber-500/10 text-amber-400" : "text-white/40 hover:text-white/70 hover:bg-white/5"}`} data-testid="tab-modules">Modules</button>
          <button onClick={() => setTab("enrollments")} className={`px-4 py-2 rounded-xl text-sm transition-all flex items-center gap-2 ${tab === "enrollments" ? "bg-amber-500/10 text-amber-400" : "text-white/40 hover:text-white/70 hover:bg-white/5"}`} data-testid="tab-enrollments"><Users className="w-4 h-4" /> Enrollments</button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-[#161821] border border-white/5 rounded-2xl p-6 mb-6 space-y-4" data-testid="module-form">
            <div className="grid md:grid-cols-3 gap-4">
              <select value={form.season} onChange={(e) => setForm({...form, season: e.target.value})} className="bg-[#0f1117] border border-white/10 text-white rounded-xl px-3 py-2" data-testid="mod-season">
                <option value="season_1">Season 1: Foundation & Assessment (Jan–Apr)</option>
                <option value="season_2">Season 2: Active Growth (May–Aug)</option>
                <option value="season_3">Season 3: Maintenance & Reflection (Sep–Dec)</option>
              </select>
              <Input type="number" value={form.week_number} onChange={(e) => setForm({...form, week_number: parseInt(e.target.value)})} placeholder="Week #" min={1} max={17} className="bg-[#0f1117] border-white/10 text-white rounded-xl" data-testid="mod-week" />
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="bg-[#0f1117] border border-white/10 text-white rounded-xl px-3 py-2" data-testid="mod-status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Module title" className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="mod-title" />
            <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Module description..." className="w-full bg-[#0f1117] border border-white/10 text-white placeholder:text-white/30 rounded-xl p-3 min-h-[80px] resize-none" required data-testid="mod-desc" />
            <textarea value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} placeholder="Detailed content..." className="w-full bg-[#0f1117] border border-white/10 text-white placeholder:text-white/30 rounded-xl p-3 min-h-[120px] resize-none" data-testid="mod-content" />
            <div className="grid md:grid-cols-3 gap-4">
              <textarea value={form.goals} onChange={(e) => setForm({...form, goals: e.target.value})} placeholder="Goals (one per line)" className="bg-[#0f1117] border border-white/10 text-white placeholder:text-white/30 rounded-xl p-3 min-h-[80px] resize-none text-sm" data-testid="mod-goals" />
              <textarea value={form.activities} onChange={(e) => setForm({...form, activities: e.target.value})} placeholder="Activities (one per line)" className="bg-[#0f1117] border border-white/10 text-white placeholder:text-white/30 rounded-xl p-3 min-h-[80px] resize-none text-sm" data-testid="mod-activities" />
              <textarea value={form.milestones} onChange={(e) => setForm({...form, milestones: e.target.value})} placeholder="Milestones (one per line)" className="bg-[#0f1117] border border-white/10 text-white placeholder:text-white/30 rounded-xl p-3 min-h-[80px] resize-none text-sm" data-testid="mod-milestones" />
            </div>
            <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl" data-testid="mod-submit">
              {saving ? "Saving..." : (editId ? "Update Module" : "Create Module")}
            </Button>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : tab === "modules" ? (
          <>
            <div className="flex gap-2 mb-4">
              {["", "season_1", "season_2", "season_3"].map(s => (
                <button key={s || "all"} onClick={() => setFilterSeason(s)} className={`px-3 py-1.5 rounded-xl text-xs transition-all ${filterSeason === s ? "bg-amber-500/10 text-amber-400" : "text-white/40 hover:text-white/70 hover:bg-white/5"}`}>
                  {s ? (seasons[s]?.name || s) : "All Seasons"}
                </button>
              ))}
            </div>
            {modules.length > 0 ? (
              <div className="space-y-3">
                {modules.map(m => {
                  const sc = SEASON_COLORS[m.season] || SEASON_COLORS.season_1;
                  return (
                    <div key={m.module_id} className={`bg-[#161821] border ${sc.border} rounded-2xl p-5`} data-testid={`mod-${m.module_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${sc.bg} ${sc.text}`}>{m.season_name}</span>
                            <span className="text-white/30 text-xs">Week {m.week_number}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${m.status === "published" ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-white/40"}`}>{m.status}</span>
                          </div>
                          <h3 className="text-white font-medium mb-1">{m.title}</h3>
                          <p className="text-white/50 text-sm mb-2">{m.description}</p>
                          <div className="flex gap-4 text-xs text-white/30">
                            <span>{m.goals?.length || 0} goals</span>
                            <span>{m.activities?.length || 0} activities</span>
                            <span>{m.milestones?.length || 0} milestones</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => toggleStatus(m)} className={`p-2 rounded-xl transition-colors ${m.status === "published" ? "text-emerald-400 hover:bg-emerald-500/10" : "text-white/30 hover:bg-white/5"}`}>
                            {m.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button onClick={() => startEdit(m)} className="p-2 rounded-xl hover:bg-blue-500/10 text-white/30 hover:text-blue-400"><PenLine className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(m.module_id)} className="p-2 rounded-xl hover:bg-red-500/10 text-white/20 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
                <TreePine className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/30">No modules yet</p>
              </div>
            )}
          </>
        ) : (
          <div className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-white/30 text-xs uppercase border-b border-white/5">
                <th className="text-left p-4">User</th><th className="text-left p-4">Season</th><th className="text-left p-4">Progress</th><th className="text-left p-4">Milestones</th><th className="text-left p-4">Enrolled</th>
              </tr></thead>
              <tbody>
                {enrollments.length > 0 ? enrollments.map(e => (
                  <tr key={e.enrollment_id} className="border-b border-white/5 last:border-0">
                    <td className="p-4"><div className="text-white/80">{e.name}</div><div className="text-white/30 text-xs">{e.email}</div></td>
                    <td className="p-4"><span className={`px-2 py-0.5 rounded-full text-xs ${SEASON_COLORS[e.current_season]?.bg} ${SEASON_COLORS[e.current_season]?.text}`}>{seasons[e.current_season]?.name || e.current_season}</span></td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-amber-400 rounded-full" style={{ width: `${e.progress_pct || 0}%` }} /></div>
                        <span className="text-white/50 text-xs">{e.progress_pct || 0}%</span>
                      </div>
                    </td>
                    <td className="p-4"><span className="text-amber-400 text-xs">{e.milestones_achieved?.length || 0}</span></td>
                    <td className="p-4 text-white/40 text-xs">{new Date(e.enrolled_at).toLocaleDateString()}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className="p-8 text-center text-white/30">No enrollments yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
